import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ApplicationResponse } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface ApplicationViewModalProps {
  isOpen: boolean;
  application: ApplicationResponse | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (appCode: string) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.75rem',
    background: 'var(--bg-secondary)',
    borderLeft: '3px solid var(--primary)',
    borderRadius: '0 0.25rem 0.25rem 0',
    marginBottom: '0.75rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  }}>
    {icon}
    {title}
  </div>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <div style={{ width: '160px', flexShrink: 0, fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
      {label}
    </div>
    <div style={{
      flex: 1,
      fontSize: '0.85rem',
      color: 'var(--text-primary)',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--form-border-color)',
      borderRadius: '0.375rem',
      padding: '0.375rem 0.625rem',
      minHeight: '2rem',
      display: 'flex',
      alignItems: 'center',
    }}>
      {value || '-'}
    </div>
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const ApplicationViewModal: React.FC<ApplicationViewModalProps> = ({
  isOpen,
  application,
  onClose,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !application) return;
    minimize({
      id: `${tabId}-view-modal-${application.appCode}`,
      title: `View - ${application.appCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(application.appCode),
    });
    onClose();
  }, [tabId, moduleName, application, minimize, onClose, onRestoreView]);

  if (!isOpen || !application) return null;

  const isActive = (application.statusCode || '').toLowerCase().includes('active');
  const statusLabel = isActive ? 'Active' : (application.statusName || 'Unknown').replace(/\s*application\s*/i, '');
  const statusClass = isActive ? 'badge-active' : 'badge-inactive';

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.settings size={20} />
            Application Details
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.info size={16} />} title="General" />
            <DetailRow label="Application ID" value={application.appId} />
            <DetailRow label="Code" value={application.appCode} />
            <DetailRow label="Name" value={application.appName} />
            <DetailRow label="Description" value={application.description} />
            <DetailRow label="Version" value={application.appVersion} />
            <DetailRow label="Status" value={<span className={`badge ${statusClass}`}>{statusLabel}</span>} />
            <DetailRow label="System Application" value={application.isSystemApp ? 'Yes — no database of its own; subscriptions carry no connection string' : 'No'} />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.link size={16} />} title="Keycloak & URLs" />
            <DetailRow label="Realm Type" value={application.keycloakRealmType === 'shared' ? 'Shared' : application.keycloakRealmType === 'dedicated' ? 'Dedicated' : application.keycloakRealmType} />
            {application.keycloakRealmType === 'dedicated' && (
              <DetailRow label="Realm Name" value={application.keycloakRealmName} />
            )}
            <DetailRow label="Callback URL" value={application.callbackUrl} />
            <DetailRow label="Logout URL" value={application.logoutUrl} />
          </div>

          {application.metadata && (
            <div style={{ marginBottom: '1.5rem' }}>
              <SectionHeader icon={<IconSystem.settings size={16} />} title="Metadata" />
              <DetailRow label="Metadata" value={
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'inherit', fontSize: 'inherit' }}>
                  {(() => {
                    try { return JSON.stringify(JSON.parse(application.metadata), null, 2); }
                    catch { return application.metadata; }
                  })()}
                </pre>
              } />
            </div>
          )}

          <div>
            <SectionHeader icon={<IconSystem.calendar size={16} />} title="Dates" />
            <DetailRow label="Created" value={application.createdAt ? new Date(application.createdAt).toLocaleString() : undefined} />
            <DetailRow label="Updated" value={application.updatedAt ? new Date(application.updatedAt).toLocaleString() : undefined} />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default ApplicationViewModal;
